import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Heart, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";

const PRESET_AMOUNTS = [10, 25, 50, 100];

const CharityPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { api } = useAuth();
    const [selectedAmount, setSelectedAmount] = useState(null);
    const [customAmount, setCustomAmount] = useState('');
    const [processing, setProcessing] = useState(false);

    // Handle return from Stripe
    useEffect(() => {
        const donationStatus = searchParams.get('donation');
        if (donationStatus === 'success') {
            toast.success('Thank you — your donation was received.');
            // Clean up URL
            window.history.replaceState({}, '', '/charity');
        } else if (donationStatus === 'cancelled') {
            // Quietly return without message
            window.history.replaceState({}, '', '/charity');
        }
    }, [searchParams]);

    const getDonationAmount = () => {
        if (customAmount && parseFloat(customAmount) > 0) {
            return parseFloat(customAmount);
        }
        return selectedAmount;
    };

    const handleDonate = async () => {
        const amount = getDonationAmount();
        if (!amount || amount < 1) {
            toast.error('Please select or enter an amount');
            return;
        }

        setProcessing(true);
        try {
            const response = await api().post('/payments/donate', {
                amount: amount,
                origin_url: window.location.origin
            });

            if (response.data.checkout_url) {
                // Redirect to Stripe
                window.location.href = response.data.checkout_url;
            } else if (response.data.mock_mode) {
                // Mock mode - show success
                toast.success('Thank you — your donation was received.');
                setSelectedAmount(null);
                setCustomAmount('');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to process donation');
        } finally {
            setProcessing(false);
        }
    };

    const handlePresetClick = (amount) => {
        setSelectedAmount(amount);
        setCustomAmount('');
    };

    const handleCustomChange = (e) => {
        setCustomAmount(e.target.value);
        setSelectedAmount(null);
    };

    return (
        <div className="min-h-screen bg-white pb-32">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-between px-4 py-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <img 
                        src={LOGO_URL} 
                        alt="Galeries Yess" 
                        className="h-8 w-auto"
                    />
                    <div className="w-9" /> {/* Spacer for centering */}
                </div>
            </div>

            <div className="pt-24 px-6">
                <h1 className="text-3xl font-heading font-light tracking-tight mb-4">
                    Charity
                </h1>
                
                <p className="text-sm text-gray-500 mb-8 italic">
                    Fashion with purpose
                </p>

                {/* Hero Image - Free a Girl */}
                <div className="aspect-square mb-8 overflow-hidden">
                    <img
                        src="https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/n3lkvhrq_Free%20a%20girl_Femke_cover_900.webp"
                        alt="Free a Girl"
                        className="w-full h-full object-cover"
                    />
                </div>

                <div className="space-y-8 text-gray-600 leading-relaxed">
                    <section>
                        <h2 className="text-lg font-heading font-medium text-black mb-3 flex items-center gap-2">
                            <Heart className="w-5 h-5 text-pink-400" />
                            Free a Girl
                        </h2>
                        <p className="text-sm">
                            Galeries Yess proudly supports <strong>Free a Girl</strong>, a Dutch foundation 
                            fighting for a world without sexual exploitation of children. Every bid, 
                            every auction — your participation directly supports their mission.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-heading font-medium text-black mb-3">
                            Their Mission
                        </h2>
                        <p className="text-sm mb-4">
                            Free a Girl rescues children from sexual exploitation and helps them rebuild 
                            their lives. Their approach works through three pillars:
                        </p>
                        <div className="space-y-3">
                            <div className="flex gap-3">
                                <span className="font-heading font-medium text-black">Rescue</span>
                                <span className="text-sm">— Liberating children from exploitation</span>
                            </div>
                            <div className="flex gap-3">
                                <span className="font-heading font-medium text-black">Empower</span>
                                <span className="text-sm">— Providing care, education & future</span>
                            </div>
                            <div className="flex gap-3">
                                <span className="font-heading font-medium text-black">Justice</span>
                                <span className="text-sm">— Ensuring perpetrators are prosecuted</span>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-lg font-heading font-medium text-black mb-3">
                            Impact
                        </h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-4 bg-gray-50">
                                <span className="block text-2xl font-heading mb-1">8,500+</span>
                                <span className="text-xs text-gray-500 uppercase tracking-wide">Children rescued</span>
                            </div>
                            <div className="text-center p-4 bg-gray-50">
                                <span className="block text-2xl font-heading mb-1">14,000+</span>
                                <span className="text-xs text-gray-500 uppercase tracking-wide">Cases reported</span>
                            </div>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-lg font-heading font-medium text-black mb-3">
                            Your Contribution
                        </h2>
                        <p className="text-sm">
                            100% of Galeries Yess auction proceeds go directly to Free a Girl. 
                            By donating your pre-loved fashion and bidding on items, you're not just 
                            giving clothes a second life — you're helping give children their freedom back.
                        </p>
                    </section>

                    {/* CTA Button - Learn More */}
                    <Button
                        onClick={() => window.open('https://www.freeagirl.nl/', '_blank', 'noopener,noreferrer')}
                        className="w-full h-12 bg-black hover:bg-black/90 text-white text-sm tracking-widest uppercase font-light"
                    >
                        Learn more about Free a Girl
                        <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>

                    {/* Donation Section */}
                    <section className="mt-10 pt-8 border-t border-gray-100">
                        <h2 className="text-lg font-heading font-medium text-black mb-2">
                            Support the Cause
                        </h2>
                        <p className="text-sm text-gray-500 mb-6">
                            Make an optional donation to support Free a Girl.
                        </p>

                        {/* Preset Amount Buttons */}
                        <div className="grid grid-cols-4 gap-2 mb-4">
                            {PRESET_AMOUNTS.map((amount) => (
                                <button
                                    key={amount}
                                    onClick={() => handlePresetClick(amount)}
                                    className={`py-3 text-sm font-medium transition-colors border ${
                                        selectedAmount === amount
                                            ? 'bg-black text-white border-black'
                                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                                    }`}
                                    data-testid={`donation-preset-${amount}`}
                                >
                                    €{amount}
                                </button>
                            ))}
                        </div>

                        {/* Custom Amount */}
                        <div className="mb-4">
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
                                <Input
                                    type="number"
                                    placeholder="Other amount"
                                    value={customAmount}
                                    onChange={handleCustomChange}
                                    className="pl-8 text-center"
                                    min="1"
                                    data-testid="donation-custom-amount"
                                />
                            </div>
                        </div>

                        {/* Donate Button */}
                        <Button
                            onClick={handleDonate}
                            disabled={processing || (!selectedAmount && !customAmount)}
                            className="w-full h-12 bg-pink-500 hover:bg-pink-600 text-white text-sm tracking-widest uppercase font-light disabled:opacity-50"
                            data-testid="donate-button"
                        >
                            {processing ? 'Processing...' : 'Donate'}
                        </Button>
                    </section>
                </div>

                <div className="mt-12 pt-8 border-t border-gray-100">
                    <p className="text-xs text-gray-400 text-center italic">
                        Women. Worth. Second Lives.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CharityPage;
